import { isUndefined } from 'lodash-es'

const anotherCopyPattern = /Copy \((\d+)\) of (.+)/
const firstCopyPattern = /Copy of (.+)/

const getOriginalName = (impurityName: string) => {
    const firstCopyMatches = firstCopyPattern.exec(impurityName)
    if (firstCopyMatches !== null) return firstCopyMatches[1]

    const anotherCopyMatches = anotherCopyPattern.exec(impurityName)
    if (anotherCopyMatches != null) return anotherCopyMatches[2]

    return impurityName
}

export const generateCopyName = <T>(
    targetName: string,
    refList: T[],
    refNameSelector: (item: T) => string,
) => {
    // find original name
    // e.g. "Copy of White Paper" -> original name = "White Paper"
    // e.g. "Copy (3) of Same Site" -> original name = "Same Site"
    targetName = getOriginalName(targetName)

    const existing = refList.find((e) => refNameSelector(e) === targetName)
    if (!existing) return targetName

    const firstCopy = refList.find(
        (e) => refNameSelector(e) === `Copy of ${targetName}`,
    )
    if (!firstCopy) return `Copy of ${targetName}`

    const anotherCopiedMatches = refList
        .map((e) => anotherCopyPattern.exec(refNameSelector(e)))
        .filter((e) => e !== null && e[2] === targetName)

    if (anotherCopiedMatches.length === 0) return `Copy (1) of ${targetName}`

    // Sort copy index to find maximum
    // 'Copy (3) of name' --> copyIndex = 3
    const copyIndexes = anotherCopiedMatches.map((match) => Number(match![1]))
    copyIndexes.sort((a, b) => a - b)

    const lastCounter = copyIndexes[copyIndexes.length - 1]

    return `Copy (${lastCounter + 1}) of ${targetName}`
}

/*
Examples of expected output:
"Buy X Get Y" -> "buy_x_get_y"
"BuyXGetY" -> "buy_x_get_y"
"Buy2Get1" -> "buy_2_get_1"
"Buy10Get12" -> "buy_10_get_12"
"Buy 10 Get 12" -> "buy_10_get_12"
"Buy 100 get10Free" -> "buy_100_get_10_free"
"Buy 100   get10Free" -> "buy_100_get_10_free"
"buy100get10free" -> "buy_100_get_10_free"
"buyTwoGetOneFree" -> "buy_two_get_one_free"
"BuyXYZGetABC" -> "buy_xyz_get_abc"
"buytwogetonefree" -> "buytwogetonefree"
"" -> ""
" " -> ""
*/
export const toSnakeCase = (str: string) => {
    if (isUndefined(str)) return ''
    return str
        .split(
            /(?<=[a-z])(?=[A-Z0-9])|(?<=[A-Z0-9])(?=[A-Z][a-z0-9])|(?<=[0-9])(?=[a-z])| /,
        )
        .filter((a) => a !== '')
        .join('_')
        .toLowerCase()
}

export const ValueOrUndefined = (str: string | null | undefined) =>
    !str || str === '' || str === null ? undefined : str

export function toTitleCase(sentence: string) {
    let str = sentence.replace(/([^\W_]+[^\s-]*) */g, function (txt) {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    })
    const lowers = ['And']
    for (let word of lowers) {
        str = str.replace(new RegExp('\\s' + word + '\\s', 'g'), function (
            txt,
        ) {
            return txt.toLowerCase()
        })
    }

    const uppers: { [key: string]: string } = { comps: 'Comps' }
    for (let key of Object.keys(uppers))
        str = str.replace(new RegExp('\\b' + key + '\\b', 'ig'), function (
            word,
        ) {
            return uppers[word] ?? word
        })

    return str
}

export const EMAIL_REGEXP = new RegExp(
    /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
)

export const IP_ADDRESS_REGEXP = new RegExp(
    /^((25[0-5]|(2[0-4]|1[0-9]|[1-9]|)[0-9])(\.(?!$)|$)){4}$/,
)
export const PHONE_REGEXP = new RegExp(
    /^[\\+]?[(]?[0-9]{3}[)]?[-\s\\.]?[0-9]{3}[-\s\\.]?[0-9]{4,6}$/,
)
